import clsx from 'clsx';
import React, { useState } from 'react';
import { BorderWidth, Colors, Radius, Shadows, ZIndex } from '@common/tokens';
import { Icon } from '@common/components';
import { SideShellPanel } from './_panel';
import { PanelStack } from './_panel-stack';
import { SideShellContext } from './_side-shell-context';
import { useSideShellPanels } from './_use-side-shell-panels';

const SIDE_WIDTH = 460;

/**
 * SideShell is a content-agnostic layout container: main content on the left
 * and a collapsible panel on the right. It owns the collapse state and both
 * toggle affordances (a collapse trigger when open, a floating expand handle
 * when collapsed), animating the panel width smoothly.
 *
 * Works in controlled (`collapsed` + `onCollapsedChange`) or uncontrolled
 * (`defaultOpen`) mode.
 *
 * The collapsible side section can host a navigation stack of panels: pass a
 * `panels` map and an `activePanel` selector (a key or a stack of keys). The
 * active panel slides in over `side` (the persistent base layer) while the main
 * `children` stays put; `SideShell.Panel` provides a standard header/body/footer
 * with a back button that pops the stack via `onActivePanelChange`. Changing
 * `activePanel` while collapsed re-opens the side section.
 */
export const SideShell = ({
  children,
  side,
  collapsed,
  defaultOpen = false,
  onCollapsedChange,
  panels,
  activePanel,
  onActivePanelChange,
  className,
  dataTestId,
}: {
  children: React.ReactNode;
  side: React.ReactNode;
  collapsed?: boolean;
  defaultOpen?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /**
   * Named panels shown inside the collapsible side section. The active one(s)
   * render over `side` (the default side content); the main `children` never
   * changes.
   */
  panels?: Record<string, React.ReactNode>;
  /**
   * Which side panel to show. A single key renders that panel; an array is a
   * navigation stack — the last key is shown on top and the rest stay mounted
   * but hidden beneath it. `undefined` (or unknown keys) shows `side`.
   */
  activePanel?: string | string[];
  /**
   * Called when the panel stack changes via the in-panel back button: receives
   * the popped stack (or `undefined` once the last panel is closed).
   */
  onActivePanelChange?: (activePanel: string | string[] | undefined) => void;
  className?: string;
  dataTestId?: string;
}) => {
  const isControlled = collapsed !== undefined;
  const [internalCollapsed, setInternalCollapsed] = useState(!defaultOpen);
  const isCollapsed = isControlled ? collapsed : internalCollapsed;

  const setCollapsed = (next: boolean) => {
    if (!isControlled) {
      setInternalCollapsed(next);
    }
    onCollapsedChange?.(next);
  };

  const { layers, contextValue } = useSideShellPanels({
    panels,
    activePanel,
    onActivePanelChange,
    isCollapsed,
    onAutoOpen: () => setCollapsed(false),
  });

  return (
    <SideShellContext.Provider value={contextValue}>
      <div
        className={clsx('wk-side-shell', className)}
        data-testid={dataTestId}
      >
        <div className="wk-side-shell__main">{children}</div>
        <section
          className={clsx('wk-side-shell__side', {
            'wk-side-shell__side--collapsed': isCollapsed,
          })}
          style={{ width: isCollapsed ? 0 : SIDE_WIDTH }}
        >
          <div className="wk-side-shell__side-clip">
            <div
              className="wk-side-shell__side-inner"
              style={{ width: SIDE_WIDTH }}
              aria-hidden={isCollapsed}
              // `aria-hidden` alone leaves collapsed content Tab-reachable;
              // `inert` also removes it from the tab order. React 18 has no
              // first-class `inert` prop, so set the attribute via spread
              // (empty string = present) — keeps the width animation intact.
              {...(isCollapsed ? { inert: '' } : {})}
            >
              {panels ? <PanelStack layers={layers}>{side}</PanelStack> : side}
            </div>
          </div>
        </section>
        <button
          type="button"
          className="wk-side-shell__toggle"
          aria-label={isCollapsed ? 'Expand panel' : 'Collapse panel'}
          aria-expanded={!isCollapsed}
          onClick={() => setCollapsed(!isCollapsed)}
        >
          <Icon
            icon={isCollapsed ? 'chevronLeft' : 'chevronRight'}
            kind="solid"
            size="sm"
          />
        </button>
      </div>
      <style jsx>{`
        .wk-side-shell {
          position: relative;
          display: flex;
          flex-direction: row;
          width: 100%;
          height: calc(100vh - var(--wk-header-height, 72px));
          overflow: hidden;
          border-top: ${BorderWidth.sm} solid ${Colors.steel};
        }
        .wk-side-shell__main {
          flex: 1;
          min-width: 0;
          min-height: 0;
          overflow: hidden;
          background: ${Colors.white};
          display: flex;
          flex-direction: column;
        }
        /* Fixed-height regions stacked above the main content (e.g. a health
           strip) keep their natural height; the last region fills the
           remaining space and owns its internal scroll (so a child like the
           calendar keeps its sticky header). A lone child is also the last
           child, so the common single-child case still fills the column. */
        .wk-side-shell__main > :global(*) {
          flex: 0 0 auto;
          min-height: 0;
        }
        .wk-side-shell__main > :global(*:last-child) {
          flex: 1;
        }
        .wk-side-shell__side {
          position: relative;
          /* Own stacking context so the panel-stack layers (which carry their
             own z-index) stay contained below the toggle, which is a sibling. */
          z-index: ${ZIndex.base};
          flex-shrink: 0;
          height: 100%;
          border-left: ${BorderWidth.sm} solid ${Colors.steel};
          background: ${Colors.steelLightest};
          transition: width 0.2s ease;
        }
        .wk-side-shell__side--collapsed {
          border-left: none;
          background: transparent;
        }
        .wk-side-shell__side-clip {
          height: 100%;
          overflow: hidden;
        }
        .wk-side-shell__side-inner {
          height: 100%;
          overflow-y: auto;
        }
        .wk-side-shell__toggle {
          position: absolute;
          top: 10px;
          right: 0;
          /* Above the side section (and its panel-stack layers) so the collapse
             affordance stays clickable while a panel is open. */
          z-index: ${ZIndex.sticky};
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 50px;
          padding: 0;
          background: ${Colors.white};
          border: ${BorderWidth.sm} solid ${Colors.steel};
          border-right: none;
          border-radius: ${Radius.sm} 0 0 ${Radius.sm};
          box-shadow: ${Shadows.xs};
          cursor: pointer;
          outline: none;
          transition:
            background 0.1s ease,
            box-shadow 0.1s ease;
        }
        .wk-side-shell__toggle:hover {
          background: ${Colors.steelLightest};
          box-shadow: ${Shadows.sm};
        }
        .wk-side-shell__toggle:focus-visible {
          outline: 2px solid ${Colors.elm};
          outline-offset: 2px;
        }
      `}</style>
    </SideShellContext.Provider>
  );
};

SideShell.Panel = SideShellPanel;
